import { useNavigate } from "react-router";
import { AFTER_MESSAGE_CHANGE, createMessage } from "@/api/mutations";
import { useAccao, texto } from "@/api/useAccao";
import type { PhotoSummary as FotoResumo } from "@historia/shared/contracts";

const campo =
  "rounded-2xl border border-ouro/30 bg-creme-50 px-4 py-2.5 text-tinta outline-none transition-colors placeholder:text-tinta-700/40 focus:border-ouro focus:ring-2 focus:ring-ouro/30";

function BotaoGuardar({ pending }: { pending: boolean }) {
  return (
    <button
      type="submit"
      disabled={pending}
      className="rounded-full bg-ouro px-6 py-3 text-sm font-semibold text-creme-50 transition-colors hover:bg-ouro-600 disabled:opacity-60"
    >
      {pending ? "A guardar…" : "Guardar mensagem"}
    </button>
  );
}

/** Formulário para adicionar uma mensagem à coleção /para-sempre. */
export function FormMensagem({ fotos }: { fotos: FotoResumo[] }) {
  const navigate = useNavigate();

  const [estado, acao, aGuardar] = useAccao(
    (fd: FormData) =>
      createMessage({
        titulo: texto(fd, "titulo"),
        corpo: texto(fd, "corpo"),
        ocasiaoData: texto(fd, "ocasiao_data") || null,
        fotoDestaqueId: texto(fd, "foto_destaque_id") || null,
      }),
    {
      invalidate: AFTER_MESSAGE_CHANGE,
      // Invalidating the messages query already guarantees the page shows the
      // new message — the old code needed router.refresh() because Next's
      // client Router Cache could serve a stale /para-sempre.
      onSuccess: () => navigate("/para-sempre"),
    },
  );

  async function submeter(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await acao(new FormData(event.currentTarget));
  }

  return (
    <form onSubmit={submeter} className="flex flex-col gap-4 text-left">
      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-tinta-700">Título / ocasião</span>
        <input
          name="titulo"
          required
          maxLength={120}
          placeholder="Ex.: Meio ano, Natal 2026…"
          className={`${campo} font-display text-lg`}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-tinta-700">Mensagem</span>
        <textarea
          name="corpo"
          required
          rows={10}
          placeholder="Escreve aqui… (deixa uma linha em branco entre parágrafos)"
          className={`${campo} resize-none leading-relaxed`}
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-tinta-700">Data (opcional)</span>
          <input type="date" name="ocasiao_data" className={campo} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-tinta-700">Foto em destaque (opcional)</span>
          <select name="foto_destaque_id" defaultValue="" className={campo}>
            <option value="">— Sem foto —</option>
            {fotos.map((f) => (
              <option key={f.id} value={f.id}>
                {f.legenda || "Foto sem legenda"}
              </option>
            ))}
          </select>
        </label>
      </div>

      {estado.erro && <p className="text-sm text-tinta">{estado.erro}</p>}

      <div className="mt-2">
        <BotaoGuardar pending={aGuardar} />
      </div>
    </form>
  );
}
